import { memo, useCallback } from 'react';
import styled from 'styled-components';
import { LuX } from 'react-icons/lu';
import { SidebarSection, SectionTitle, SectionContent } from './FlightPlannerStyles';
import FlightPlansList from './FlightPlansList';

const DeleteButton = styled.button`
	background: none;
	border: none;
	color: #ff6b6b;
	cursor: pointer;
	font-size: 1.2rem;
	padding: 0 0.25rem;
	opacity: 0.7;

	&:hover {
		opacity: 1;
	}
`;

// Airspace data for layer counts
import {
	airports,
	militaryZones,
	heliports,
	governmentZones,
} from '../../../../../utils/map/norwegianAirspace';

/**
 * Left sidebar — Layers, measurements, and saved flight plans.
 * Always visible regardless of workflow step.
 */
const LayersPanel = ({
	airspaceLayers,
	onAirspaceLayersChange,
	layerOpacity,
	onLayerOpacityChange,
	propertyLineWidth,
	onPropertyLineWidthChange,
	customMapConfig,
	measurements,
	onDeleteMeasurement,
	editingPlanId,
	onLoadPlan,
}) => {
	const toggleLayer = useCallback(
		key => onAirspaceLayersChange(prev => ({ ...prev, [key]: !prev[key] })),
		[onAirspaceLayersChange]
	);

	const updateOpacity = useCallback(
		(key, value) => onLayerOpacityChange(prev => ({ ...prev, [key]: value })),
		[onLayerOpacityChange]
	);

	return (
		<>
			<SidebarSection>
				<SectionTitle>Layers</SectionTitle>
				<SectionContent>
					<div style={{ marginBottom: '0.75rem' }}>
						<div
							style={{
								fontSize: '0.75rem',
								color: '#888',
								textTransform: 'uppercase',
								letterSpacing: '0.5px',
								fontWeight: 600,
								marginBottom: '0.5rem',
							}}>
							No-Fly Zones (Norway)
						</div>
						{[
							{
								key: 'airports',
								label: 'Airports',
								color: '#FF0000',
								count: airports.length,
							},
							{
								key: 'military',
								label: 'Military',
								color: '#8B0000',
								count: militaryZones.length,
							},
							{
								key: 'heliports',
								label: 'Heliports',
								color: '#FF6600',
								count: heliports.length,
							},
							{
								key: 'government',
								label: 'Government',
								color: '#4B0082',
								count: governmentZones.length,
							},
							{
								key: 'nature',
								label: 'Nature Reserves',
								color: '#228B22',
								count: 'WMS',
								isWms: true,
							},
						].map(layer => (
							<div key={layer.key}>
								<div
									onClick={() => toggleLayer(layer.key)}
									style={{
										display: 'flex',
										alignItems: 'center',
										padding: '0.4rem 0.5rem',
										borderRadius: '4px',
										cursor: 'pointer',
										marginBottom:
											layer.isWms && airspaceLayers[layer.key] ? '0' : '2px',
										backgroundColor: airspaceLayers[layer.key]
											? 'rgba(255,255,255,0.05)'
											: 'transparent',
									}}>
									<div
										style={{
											width: '12px',
											height: '12px',
											borderRadius: '3px',
											backgroundColor: layer.color,
											marginRight: '8px',
											opacity: airspaceLayers[layer.key] ? 1 : 0.4,
										}}
									/>
									<input
										type="checkbox"
										checked={airspaceLayers[layer.key]}
										onChange={() => toggleLayer(layer.key)}
										onClick={e => e.stopPropagation()}
										style={{
											marginRight: '8px',
											accentColor: '#4CAF50',
										}}
									/>
									<span
										style={{
											flex: 1,
											fontSize: '0.85rem',
											color: airspaceLayers[layer.key] ? '#fff' : '#888',
										}}>
										{layer.label}
									</span>
									<span style={{ fontSize: '0.75rem', color: '#666' }}>
										{layer.count}
									</span>
								</div>
								{layer.isWms && airspaceLayers[layer.key] && (
									<div
										style={{
											padding: '0.25rem 0.5rem 0.5rem 1.5rem',
											backgroundColor: 'rgba(255,255,255,0.02)',
											marginBottom: '2px',
											overflow: 'hidden',
										}}>
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
											}}>
											<span
												style={{
													fontSize: '0.75rem',
													color: '#888',
													minWidth: '45px',
												}}>
												Opacity
											</span>
											<input
												type="range"
												min="0"
												max="100"
												value={layerOpacity[layer.key] * 100}
												onChange={e =>
													updateOpacity(layer.key, e.target.value / 100)
												}
												onClick={e => e.stopPropagation()}
												style={{
													flex: 1,
													minWidth: 0,
													accentColor: layer.color,
													height: '4px',
												}}
											/>
											<span
												style={{
													fontSize: '0.75rem',
													color: '#888',
													minWidth: '30px',
												}}>
												{Math.round(layerOpacity[layer.key] * 100)}%
											</span>
										</div>
									</div>
								)}
							</div>
						))}
					</div>
					<div style={{ marginBottom: '0.75rem', marginTop: '0.75rem' }}>
						<div
							style={{
								fontSize: '0.75rem',
								color: '#888',
								textTransform: 'uppercase',
								letterSpacing: '0.5px',
								fontWeight: 600,
								marginBottom: '0.5rem',
							}}>
							Property Data
						</div>
						<div
							onClick={() => toggleLayer('property')}
							style={{
								display: 'flex',
								alignItems: 'center',
								padding: '0.4rem 0.5rem',
								borderRadius: '4px',
								cursor: 'pointer',
								marginBottom: airspaceLayers.property ? '0' : '2px',
								backgroundColor: airspaceLayers.property
									? 'rgba(255,255,255,0.05)'
									: 'transparent',
							}}>
							<div
								style={{
									width: '12px',
									height: '12px',
									borderRadius: '3px',
									backgroundColor: '#FFA500',
									marginRight: '8px',
									opacity: airspaceLayers.property ? 1 : 0.4,
								}}
							/>
							<input
								type="checkbox"
								checked={airspaceLayers.property}
								onChange={() => toggleLayer('property')}
								onClick={e => e.stopPropagation()}
								style={{ marginRight: '8px', accentColor: '#4CAF50' }}
							/>
							<span
								style={{
									flex: 1,
									fontSize: '0.85rem',
									color: airspaceLayers.property ? '#fff' : '#888',
								}}>
								Property Boundaries
							</span>
							<span style={{ fontSize: '0.75rem', color: '#666' }}>WMS</span>
						</div>
						{airspaceLayers.property && (
							<div
								style={{
									padding: '0.25rem 0.5rem 0.5rem 1.5rem',
									backgroundColor: 'rgba(255,255,255,0.02)',
									marginBottom: '2px',
									overflow: 'hidden',
								}}>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
										marginBottom: '6px',
									}}>
									<span
										style={{
											fontSize: '0.75rem',
											color: '#888',
											minWidth: '45px',
										}}>
										Opacity
									</span>
									<input
										type="range"
										min="0"
										max="100"
										value={layerOpacity.property * 100}
										onChange={e =>
											updateOpacity('property', e.target.value / 100)
										}
										onClick={e => e.stopPropagation()}
										style={{
											flex: 1,
											minWidth: 0,
											accentColor: '#FFA500',
											height: '4px',
										}}
									/>
									<span
										style={{
											fontSize: '0.75rem',
											color: '#888',
											minWidth: '30px',
										}}>
										{Math.round(layerOpacity.property * 100)}%
									</span>
								</div>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}>
									<span
										style={{
											fontSize: '0.75rem',
											color: '#888',
											minWidth: '45px',
										}}>
										Line
									</span>
									<input
										type="range"
										min="1"
										max="4"
										step="0.5"
										value={propertyLineWidth}
										onChange={e =>
											onPropertyLineWidthChange(parseFloat(e.target.value))
										}
										onClick={e => e.stopPropagation()}
										style={{
											flex: 1,
											minWidth: 0,
											accentColor: '#FFA500',
											height: '4px',
										}}
									/>
									<span
										style={{
											fontSize: '0.75rem',
											color: '#888',
											minWidth: '30px',
										}}>
										{propertyLineWidth}px
									</span>
								</div>
							</div>
						)}
					</div>
					{customMapConfig?.url && (
						<div style={{ marginBottom: '0.75rem', marginTop: '0.75rem' }}>
							<div
								style={{
									fontSize: '0.75rem',
									color: '#888',
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
									fontWeight: 600,
									marginBottom: '0.5rem',
								}}>
								Customer Map
							</div>
							<div
								onClick={() => toggleLayer('customMap')}
								style={{
									display: 'flex',
									alignItems: 'center',
									padding: '0.4rem 0.5rem',
									borderRadius: '4px',
									cursor: 'pointer',
									marginBottom: airspaceLayers.customMap ? '0' : '2px',
									backgroundColor: airspaceLayers.customMap
										? 'rgba(255,255,255,0.05)'
										: 'transparent',
								}}>
								<div
									style={{
										width: '12px',
										height: '12px',
										borderRadius: '3px',
										backgroundColor: '#00BCD4',
										marginRight: '8px',
										opacity: airspaceLayers.customMap ? 1 : 0.4,
									}}
								/>
								<input
									type="checkbox"
									checked={airspaceLayers.customMap}
									onChange={() => toggleLayer('customMap')}
									onClick={e => e.stopPropagation()}
									style={{ marginRight: '8px', accentColor: '#4CAF50' }}
								/>
								<span
									style={{
										flex: 1,
										fontSize: '0.85rem',
										color: airspaceLayers.customMap ? '#fff' : '#888',
									}}>
									{customMapConfig.layerName || 'Custom Map'}
								</span>
								<span style={{ fontSize: '0.75rem', color: '#666' }}>ArcGIS</span>
							</div>
							{airspaceLayers.customMap && (
								<div
									style={{
										padding: '0.25rem 0.5rem 0.5rem 1.5rem',
										backgroundColor: 'rgba(255,255,255,0.02)',
										marginBottom: '2px',
										overflow: 'hidden',
									}}>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '8px',
										}}>
										<span
											style={{
												fontSize: '0.75rem',
												color: '#888',
												minWidth: '45px',
											}}>
											Opacity
										</span>
										<input
											type="range"
											min="0"
											max="100"
											value={layerOpacity.customMap * 100}
											onChange={e =>
												updateOpacity('customMap', e.target.value / 100)
											}
											onClick={e => e.stopPropagation()}
											style={{
												flex: 1,
												minWidth: 0,
												accentColor: '#00BCD4',
												height: '4px',
											}}
										/>
										<span
											style={{
												fontSize: '0.75rem',
												color: '#888',
												minWidth: '30px',
											}}>
											{Math.round(layerOpacity.customMap * 100)}%
										</span>
									</div>
								</div>
							)}
						</div>
					)}
					<div style={{ fontSize: '0.75rem', color: '#666', fontStyle: 'italic' }}>
						Click zones on map for details
					</div>
				</SectionContent>
			</SidebarSection>

			{measurements.length > 0 && (
				<SidebarSection>
					<SectionTitle>Measurements</SectionTitle>
					<SectionContent>
						{measurements.map((m, idx) => (
							<div
								key={m.id || idx}
								style={{
									padding: '0.5rem',
									backgroundColor: 'rgba(255, 165, 0, 0.1)',
									borderRadius: '4px',
									marginBottom: '0.5rem',
									borderLeft: `3px solid ${m.color || '#ffa500'}`,
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}>
								<div style={{ flex: 1 }}>
									<div
										style={{
											fontSize: '0.75rem',
											color: '#888',
											textTransform: 'uppercase',
										}}>
										{m.type === 'distance'
											? '⎯ Distance'
											: m.type === 'area'
												? '▢ Area'
												: '◯ Diameter'}
									</div>
									<div
										style={{
											fontSize: '1rem',
											fontWeight: '600',
											color: '#fff',
										}}>
										{m.value.toFixed(2)} {m.unit}
									</div>
								</div>
								<DeleteButton
									onClick={() => onDeleteMeasurement(m.id)}
									title="Delete measurement">
									<LuX size={14} />
								</DeleteButton>
							</div>
						))}
					</SectionContent>
				</SidebarSection>
			)}

			<SidebarSection>
				<SectionTitle>Saved Flight Plans</SectionTitle>
				<SectionContent>
					<FlightPlansList onLoadPlan={onLoadPlan} activePlanId={editingPlanId} />
				</SectionContent>
			</SidebarSection>
		</>
	);
};

export default memo(LayersPanel);
